import type { Container } from "pixi.js";
import type { BetLinesPort } from "@slot/game-shell";
import type { PresentationTaskRunner } from "@slot/presentation";
import { POTS, type JackpotTier, type PotKind } from "../../rules/symbols";
import type { EffectsLayer } from "../../scene/EffectsLayer";
import type { ShineSweeper } from "../../scene/ShineSweeper";
import { LINES_TUNING } from "../../tuning/featureTuning";
import { SHINE_TUNING, type GlintTuning, type SparkleTuning } from "../../tuning/shineTuning";

type Point = { x: number; y: number };
type Box = { x: number; y: number; width: number; height: number };
type PlaqueSpot = "grand" | "major" | "minor" | "mini";
const PLAQUE_SPOTS: readonly PlaqueSpot[] = ["grand", "major", "minor", "mini"];

/** A mouth's rim is an ellipse this much flatter than it is wide (seen from the front, a little above). */
const MOUTH_RIM_FLATTEN = 0.2;

/** What the shine plays on: the elements it glints across, the rims its sparkles twinkle on (the composition's space). */
export interface ShineTargets {
  /** The gold frame, and its gold. */
  frame(): { node: Container; rim: Box } | null;
  /** The row frame around the active lines' rows while it shows, and its box. */
  rowFrame(): { node: Container; rim: Box } | null;
  /** A jackpot plaque, and its bar's rim (GRAND's spot shows ROYAL while GROW plays). */
  plaque(spot: PlaqueSpot): { node: Container; rim: Box };
  /** A pot, and its mouth's rim. */
  pot(kind: PotKind): { node: Container; rim: { x: number; y: number; halfWidth: number } };
  /** The coins at rest on the board now. */
  coins(): readonly Container[];
}

/** Where the elements' turns come round, in order (a turn with nothing to show now passes to the next). */
const AMBIENT_TURNS = ["plaque:grand", "frame", "pot:life", "plaque:minor", "coins", "pot:multi", "plaque:major", "rowFrame", "pot:grow", "plaque:mini"] as const;
type Turn = (typeof AMBIENT_TURNS)[number];

/**
 * The shine on the gold (the user, 2026-10-06): glints sweeping across the frames, the jackpot plaques, the pots and the
 * coins, and sparkles twinkling on their rims.
 *
 * While the game waits for the player — the base game idle (the bet controls' rule: no round, no autoplay, no replay),
 * or the bonus before a respin — the elements take turns, a soft glint every few seconds and a sparkle now and then, on
 * the ambient timeline: the UI's runner, which never speeds up with the round. The round's moments shine brighter, at
 * once, on the round's runner: the bonus's trigger (the frame, then each pot), a pot charging, a jackpot won or paid (its
 * plaque), a coin landing (not after a slam). A change of the lines glints the row frame as it settles (the base game's,
 * on the ambient timeline). No shine is work a round waits for, nor in the round's scope: a glint may outlive the action
 * it lit, and a slam latched on the scope outside an action's window would snap the next action. The slam takes the
 * moments' glints off itself (`slam`).
 */
export class ShinePresenter {
  private readonly names = new Map<Container, string>();
  private readonly coinGlints = new Set<Container>();
  /** The round's moments' glints (a slam takes them off). */
  private readonly momentGlints = new Set<Container>();
  private bonusWaiting = false;
  private disposed = false;
  /** How long the game has waited now, and when the next glint and sparkle fall in that wait. */
  private waited = 0;
  private nextGlintAt: number;
  private nextSparkleAt: number;
  private turn = 0;

  public constructor(
    private readonly input: {
      sweeper: ShineSweeper;
      targets: ShineTargets;
      fx: EffectsLayer;
      /** The round's runner: the moments' shine. */
      runner: PresentationTaskRunner;
      /** The UI's runner: the waiting game's shine. */
      ambientRunner: PresentationTaskRunner;
      betLines: BetLinesPort;
      random?: () => number;
    },
  ) {
    this.nextGlintAt = SHINE_TUNING.ambient.settleSeconds;
    this.nextSparkleAt = this.firstSparkleAt();
    let last = 0;
    void input.ambientRunner.tickInScope(
      "",
      (elapsed) => {
        const dt = elapsed - last;
        last = elapsed;
        this.tick(dt);
      },
      () => this.disposed,
    );
  }

  /** The bonus waits for the player's Spin before a respin (`true`), or the wait is over. */
  public setBonusWaiting(waiting: boolean): void {
    this.bonusWaiting = waiting;
  }

  /** The lines changed (decision 56): the row frame glints as it settles on its new rows, its corners sparkling. */
  public linesChanged(): void {
    const timeline = { runner: this.input.ambientRunner };
    void this.input.ambientRunner.sleep(LINES_TUNING.frameResize.seconds * 0.6).then(() => {
      const row = this.input.targets.rowFrame();
      if (!row || this.disposed) return;
      this.glint(row.node, "rowFrame", SHINE_TUNING.event.glint, timeline);
      void this.sparkleAround(corners(row.rim), SHINE_TUNING.event.sparkle, timeline);
    });
  }

  /** The bonus is triggered: the frame glints, then each pot in turn, the frame's corners sparkling. */
  public bonusTriggered(): void {
    void (async () => {
      const frame = this.input.targets.frame();
      if (frame) {
        this.glint(frame.node, "frame", SHINE_TUNING.event.glint);
        void this.sparkleAround([...corners(frame.rim), ...midpoints(frame.rim)], SHINE_TUNING.event.sparkle);
      }
      for (const kind of POTS) {
        await this.input.runner.sleep(SHINE_TUNING.event.staggerSeconds);
        if (this.disposed) return;
        this.glint(this.input.targets.pot(kind).node, `pot:${kind}`, SHINE_TUNING.event.glint);
      }
    })();
  }

  /** A gem lands in `kind`'s pot, or the pot erupts into its booster: the pot glints, its mouth sparkling. */
  public potCharged(kind: PotKind): void {
    const pot = this.input.targets.pot(kind);
    this.glint(pot.node, `pot:${kind}`, SHINE_TUNING.event.glint);
    void this.sparkleAround(this.mouthPoints(pot.rim, 3), SHINE_TUNING.event.sparkle);
  }

  /** A jackpot is won (a full board): its plaque glints, sparkles running round its bar. */
  public jackpotWon(tier: JackpotTier): void {
    const plaque = this.input.targets.plaque(tier === "royal" ? "grand" : tier);
    this.glint(plaque.node, `plaque:${tier === "royal" ? "grand" : tier}`, SHINE_TUNING.event.glint);
    void this.sparkleAround(aroundBox(plaque.rim, SHINE_TUNING.event.sparkleCount), SHINE_TUNING.event.sparkle);
  }

  /** A jackpot coin is paid at the bonus's end: its plaque glints as its number leaves it. */
  public jackpotPaid(tier: JackpotTier): void {
    const spot = tier === "royal" ? "grand" : tier;
    this.glint(this.input.targets.plaque(spot).node, `plaque:${spot}`, SHINE_TUNING.event.glint);
  }

  /** A coin landed (not after the player's slam): a quick glint across it. */
  public coinLanded(node: Container): void {
    this.glint(node, "coin", SHINE_TUNING.coin.land);
  }

  /** A new roll: the coins' glints come off (their views go back to the pool as the cells roll). */
  public newRoll(): void {
    this.input.sweeper.stopWhere((node) => this.coinGlints.has(node));
    this.coinGlints.clear();
  }

  /** The player's slam: the round's moments' glints come off at once. */
  public slam(): void {
    this.input.sweeper.stopWhere((node) => this.momentGlints.has(node));
    this.momentGlints.clear();
  }

  /** What glints now, by name (`frame`, `rowFrame`, `plaque:grand`, `pot:life`, `coin`): the dev hook's read. */
  public get glinting(): string[] {
    return this.input.sweeper.glinting.map((node) => this.names.get(node) ?? "?");
  }

  /** Of those, the round's moments' (the dev hook's read). */
  public get moments(): string[] {
    return this.input.sweeper.glinting.filter((node) => this.momentGlints.has(node)).map((node) => this.names.get(node) ?? "?");
  }

  public dispose(): void {
    this.disposed = true;
  }

  private isWaiting(): boolean {
    return this.bonusWaiting || this.input.betLines.getSnapshot().canChange;
  }

  private tick(dt: number): void {
    if (this.disposed) return;
    if (!this.isWaiting()) {
      this.waited = 0;
      this.nextGlintAt = SHINE_TUNING.ambient.settleSeconds;
      this.nextSparkleAt = this.firstSparkleAt();
      return;
    }
    this.waited += dt;
    if (this.waited >= this.nextGlintAt) {
      this.nextGlintAt += SHINE_TUNING.ambient.everySeconds;
      this.ambientGlint();
    }
    if (this.waited >= this.nextSparkleAt) {
      this.nextSparkleAt += this.sparkleGap();
      this.ambientSparkle();
    }
  }

  /** The next element whose turn it is (a turn with nothing to show passes on) glints softly. */
  private ambientGlint(): void {
    for (let tries = 0; tries < AMBIENT_TURNS.length; tries++) {
      const turn = AMBIENT_TURNS[this.turn % AMBIENT_TURNS.length]!;
      this.turn += 1;
      const target = this.turnTarget(turn);
      if (!target) continue;
      const look = target.name === "coin" ? SHINE_TUNING.coin.ambient : SHINE_TUNING.ambient.glint;
      this.glint(target.node, target.name, look, { runner: this.input.ambientRunner });
      return;
    }
  }

  private turnTarget(turn: Turn): { node: Container; name: string } | null {
    const { targets } = this.input;
    if (turn === "frame") return named(targets.frame()?.node ?? null, "frame");
    if (turn === "rowFrame") return named(targets.rowFrame()?.node ?? null, "rowFrame");
    if (turn === "coins") {
      const coins = targets.coins();
      return coins.length > 0 ? { node: coins[Math.floor(this.random() * coins.length)]!, name: "coin" } : null;
    }
    const [kind, which] = turn.split(":") as ["plaque" | "pot", string];
    return kind === "plaque" ? { node: targets.plaque(which as PlaqueSpot).node, name: turn } : { node: targets.pot(which as PotKind).node, name: turn };
  }

  /** A sparkle on a random gold rim: the frame's, a plaque's bar's, a pot's mouth's. */
  private ambientSparkle(): void {
    const { targets } = this.input;
    const rims: (() => Point | null)[] = [
      () => {
        const frame = targets.frame();
        return frame ? onBox(frame.rim, this.random()) : null;
      },
      ...PLAQUE_SPOTS.map((spot) => () => onBox(targets.plaque(spot).rim, this.random())),
      ...POTS.map((kind) => () => this.mouthPoints(targets.pot(kind).rim, 1)[0]!),
    ];
    const at = rims[Math.floor(this.random() * rims.length)]!();
    if (at) this.twinkle(at, SHINE_TUNING.ambient.sparkle, { runner: this.input.ambientRunner });
  }

  /** A glint across `node`, on `timeline` (the ambient one's), else the round's runner: one of its moments. */
  private glint(node: Container, name: string, look: GlintTuning, timeline?: { runner: PresentationTaskRunner }): void {
    this.names.set(node, name);
    if (name === "coin") this.coinGlints.add(node);
    if (timeline) this.momentGlints.delete(node);
    else this.momentGlints.add(node);
    void this.input.sweeper.sweep(node, look, timeline?.runner ?? this.input.runner).then(() => {
      if (this.input.sweeper.glinting.includes(node)) return;
      this.names.delete(node);
      this.coinGlints.delete(node);
      this.momentGlints.delete(node);
    });
  }

  private twinkle(at: Point, look: SparkleTuning, timeline?: { runner: PresentationTaskRunner }): void {
    this.input.fx.twinkle({ at, size: look.size, tint: look.tint, seconds: look.seconds, timeline: timeline ?? { runner: this.input.runner } });
  }

  /** Sparkles at `points` one after another (on `timeline`, else the round's runner). */
  private async sparkleAround(points: readonly Point[], look: SparkleTuning, timeline?: { runner: PresentationTaskRunner }): Promise<void> {
    const runner = timeline?.runner ?? this.input.runner;
    for (let i = 0; i < points.length; i++) {
      if (i > 0) await runner.sleep(SHINE_TUNING.event.sparkleStaggerSeconds);
      if (this.disposed) return;
      this.twinkle(points[i]!, look, timeline);
    }
  }

  private mouthPoints(rim: { x: number; y: number; halfWidth: number }, count: number): Point[] {
    return Array.from({ length: count }, () => {
      const angle = this.random() * Math.PI * 2;
      return { x: rim.x + Math.cos(angle) * rim.halfWidth, y: rim.y + Math.sin(angle) * rim.halfWidth * MOUTH_RIM_FLATTEN };
    });
  }

  private firstSparkleAt(): number {
    return SHINE_TUNING.ambient.settleSeconds + this.sparkleGap();
  }

  private sparkleGap(): number {
    const [low, high] = SHINE_TUNING.ambient.sparkleEverySeconds;
    return low + (high - low) * this.random();
  }

  private random(): number {
    return (this.input.random ?? Math.random)();
  }
}

function named(node: Container | null, name: string): { node: Container; name: string } | null {
  return node ? { node, name } : null;
}

/** A point on a box's edge, `t` (0..1) of the way round it from its top-left corner, clockwise. */
function onBox(box: Box, t: number): Point {
  const perimeter = 2 * (box.width + box.height);
  let d = t * perimeter;
  if (d < box.width) return { x: box.x + d, y: box.y };
  d -= box.width;
  if (d < box.height) return { x: box.x + box.width, y: box.y + d };
  d -= box.height;
  if (d < box.width) return { x: box.x + box.width - d, y: box.y + box.height };
  d -= box.width;
  return { x: box.x, y: box.y + box.height - d };
}

/** `count` points evenly round a box's edge. */
function aroundBox(box: Box, count: number): Point[] {
  return Array.from({ length: count }, (_, i) => onBox(box, (i + 0.5) / count));
}

function corners(box: Box): Point[] {
  return [
    { x: box.x, y: box.y },
    { x: box.x + box.width, y: box.y },
    { x: box.x + box.width, y: box.y + box.height },
    { x: box.x, y: box.y + box.height },
  ];
}

function midpoints(box: Box): Point[] {
  return [
    { x: box.x + box.width / 2, y: box.y },
    { x: box.x + box.width / 2, y: box.y + box.height },
  ];
}
